/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IDobbiesNewStore, IDobbiesNewStoreLocatorViewProps } from './dobbies-new-store-locator';
import { createStoresDataArray } from './utils/store-utils';
export interface IDobbiesStoresByRegions {
    regionName: string;
    stores: IDobbiesNewStore[][];
}

export default (props: IDobbiesNewStoreLocatorViewProps) => {
    const [selectedAlphabet, setSelectedAlphabet] = React.useState('');
    const [storesByRegions, setStoresByRegions] = React.useState<IDobbiesStoresByRegions[]>([]);
    const [noStoreAlphabetsState, setNoStoreAlphabets] = React.useState<string[]>([]);
    const alphabetsList = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z'
    ];

    const [englandSectionCollapsed, setEnglandSectionCollapsed] = React.useState(false);
    const [scotlandSectionCollapsed, setScotlandSectionCollapsed] = React.useState(false);
    const [niSectionCollapsed, setNISectionCollapsed] = React.useState(false);
    const [walesSectionCollapsed, setWalesSectionCollapsed] = React.useState(false);
    const [ldSectionCollapsed, setLDSectionCollapsed] = React.useState(false);

    const collapseUnCollapseRegion = (regionName: string) => {
        if (regionName.toLowerCase() === 'england') {
            setEnglandSectionCollapsed(!englandSectionCollapsed);
        }
        if (regionName.toLowerCase() === 'scotland') {
            setScotlandSectionCollapsed(!scotlandSectionCollapsed);
        }
        if (regionName.toLowerCase() === 'northern ireland') {
            setNISectionCollapsed(!niSectionCollapsed);
        }
        if (regionName.toLowerCase() === 'wales') {
            setWalesSectionCollapsed(!walesSectionCollapsed);
        }
        if (regionName.toLowerCase() === 'little dobbies') {
            setLDSectionCollapsed(!ldSectionCollapsed);
        }
    };

    const shouldShowStoresForRegion = (regionName: string) => {
        if (regionName.toLowerCase() === 'england') {
            return !englandSectionCollapsed;
        }
        if (regionName.toLowerCase() === 'scotland') {
            return !scotlandSectionCollapsed;
        }
        if (regionName.toLowerCase() === 'northern ireland') {
            return !niSectionCollapsed;
        }
        if (regionName.toLowerCase() === 'wales') {
            return !walesSectionCollapsed;
        }
        if (regionName.toLowerCase() === 'little dobbies') {
            return !ldSectionCollapsed;
        }
        return false;
    };

    const showNoResults = () => {
        for (const sbyr of storesByRegions) {
            if (sbyr.stores.length && sbyr.stores[0].length) {
                return false;
            }
        }
        if (props.storesByRegions && props.storesByRegions.length) {
            for (const sbyr of props.storesByRegions) {
                if (sbyr.stores.length && sbyr.stores[0].length) {
                    return false;
                }
            }
        }
        return true;
    };

    const isANoStoreAlphabet = (alphabet: string) => {
        return noStoreAlphabetsState.includes(alphabet);
    };

    const updateRegions = () => {
        const allStoresByRegions: IDobbiesStoresByRegions[] = [];
        const configRegions = props.config.storesByRegionsData || [];
        const nostoresAlphabets: string[] = [];
        if (configRegions && configRegions.length) {
            for (const cr of configRegions) {
                if (cr.regionName && cr.storesInfo) {
                    const stores = createStoresDataArray(cr.regionName, cr.storesInfo);
                    allStoresByRegions.push({ regionName: cr.regionName, stores: stores });
                }
            }
        }
        for (const alpha of alphabetsList) {
            let hasStores = false;
            for (const asbr of allStoresByRegions) {
                if (hasStores) {
                    break;
                }
                for (const st of asbr.stores) {
                    if (hasStores) {
                        break;
                    }
                    for (const stCol of st) {
                        if (stCol.name?.toLowerCase().startsWith(alpha.toLowerCase())) {
                            hasStores = true;
                            break;
                        }
                    }
                }
            }
            if (!hasStores) {
                nostoresAlphabets.push(alpha);
            }
        }

        setStoresByRegions(allStoresByRegions);
        setNoStoreAlphabets(nostoresAlphabets);
    };

    const updateSelectedAlphabetView = () => {
        const allStoresByRegions: IDobbiesStoresByRegions[] = [];
        const configRegions = props.config.storesByRegionsData || [];
        if (configRegions && configRegions.length) {
            for (const cr of configRegions) {
                if (cr.regionName && cr.storesInfo) {
                    const stores = createStoresDataArray(
                        cr.regionName,
                        cr.storesInfo.filter(si => si.storeName?.startsWith(selectedAlphabet))
                    );
                    allStoresByRegions.push({ regionName: cr.regionName, stores: stores });
                }
            }
        }

        setStoresByRegions(allStoresByRegions);
    };

    React.useEffect(() => {
        if (selectedAlphabet) {
            updateSelectedAlphabetView();
        } else if (selectedAlphabet === '') {
            updateRegions();
        }
    }, [selectedAlphabet]);

    React.useEffect(() => {
        updateRegions();
    }, []);

    React.useEffect(() => {
        console.log('Stores By Regions Updated', storesByRegions);
    }, [storesByRegions]);

    const getJSXElementOfStores = (stores: IDobbiesNewStore[][]): JSX.Element => {
        if (stores && stores.length) {
            const columns = stores[0].length;
            const rows = stores.length;
            const allColumns: IDobbiesNewStore[][] = [];
            for (let i = 0; i < columns; i++) {
                allColumns.push([]);
                for (let j = 0; j < rows; j++) {
                    if (stores[j][i]) {
                        allColumns[i][j] = stores[j][i];
                    }
                }
            }
            return (
                <>
                    {allColumns.map((col, index) => {
                        return (
                            <ul key={index + 8}>
                                {col.map((elem, index) => {
                                    return (
                                        <li key={index}>
                                            {/* <a href={elem.pageUrl ? elem.pageUrl : '#'}>{elem.name}</a> */}
                                            <a
                                                onClick={() => {
                                                    window.location.href = elem.pageUrl || '';
                                                }}
                                            >
                                                {elem.name}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        );
                    })}
                </>
            );
        }

        return <></>;
    };

    const cachedStores = storesByRegions.length > 1 ? storesByRegions : props.storesByRegions;
    console.log('Cached Stores', cachedStores, props.storesByRegions);
    return (
        <div className='store-locator'>
            <h2 className='text-center'>Store locator</h2>
            <div className='store-locator__head'>
                <p>Select the area’s first letter to find your nearest store</p>
                <ul className='store-locator__head-alphabet'>
                    {alphabetsList.map((alphabet, index) => {
                        return (
                            <li
                                id={`store-select-${alphabet}`}
                                className={isANoStoreAlphabet(alphabet) ? 'disabled store-alphabit' : 'enabled store-alphabit'}
                                key={index}
                                onClick={() => {
                                    setSelectedAlphabet(alphabet);
                                    const selectedAlphabets = document.getElementsByClassName('store-selected') as HTMLCollection;
                                    if (selectedAlphabets && selectedAlphabets.length) {
                                        for (let iter = 0; iter < selectedAlphabets.length; iter++) {
                                            selectedAlphabets[iter].classList.remove('store-selected');
                                        }
                                    }
                                    const brandSelected = document.getElementById(`store-select-${alphabet}`);
                                    brandSelected?.classList.add('store-selected');
                                }}
                            >
                                <a href='javascript:void(0)'>{alphabet}</a>
                            </li>
                        );
                    })}
                </ul>
                <a
                    href='javascript:void(0)'
                    onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                        setSelectedAlphabet('');
                        const allAlphabets = document.querySelectorAll(`.store-alphabit`);
                        if (allAlphabets && allAlphabets.length) {
                            for (let iter = 0; iter < allAlphabets.length; iter++) {
                                allAlphabets[iter].classList.remove('store-selected');
                            }
                        }
                    }}
                >
                    See all stores
                </a>
            </div>
            <div>
                {cachedStores?.length ? (
                    cachedStores.map(storeByRegion => {
                        return (
                            <>
                                {storeByRegion.stores && storeByRegion.stores.length && storeByRegion.stores[0].length ? (
                                    <div>
                                        <p
                                            className={
                                                shouldShowStoresForRegion(storeByRegion.regionName)
                                                    ? 'store-locator__block-header active'
                                                    : 'store-locator__block-header'
                                            }
                                            onClick={() => {
                                                collapseUnCollapseRegion(storeByRegion.regionName);
                                            }}
                                        >
                                            {storeByRegion.regionName}{' '}
                                        </p>
                                        {shouldShowStoresForRegion(storeByRegion.regionName) ? (
                                            <div
                                                className={
                                                    shouldShowStoresForRegion(storeByRegion.regionName)
                                                        ? 'store-locator__block show'
                                                        : 'store-locator__block collapse'
                                                }
                                            >
                                                <>{getJSXElementOfStores(storeByRegion.stores)}</>
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </>
                        );
                    })
                ) : (
                    <></>
                )}
                {showNoResults() ? <h2 className='text-center my-5'>No store found</h2> : <></>}
            </div>
        </div>
    );
};
